<template>
  <Modal
    ref="popupModal"
    type="info"
    :title="popupData?.title"
    negative-action-text="Kapat"
    positive-action-text="Uygula"
    :with-closer="true"
    :click-to-close="false"
    :is-positive-action-pending="isSaveLoading"
    :is-positive-action-button-disabled="isSaveLoading"
    :modal-style="{ width: '520px' }"
    @on-negative-action-click="handleClose"
    @on-positive-action-click="handleApply"
    @modal-closed="onModalClosed"
  >
    <template #body>
      <div class="saturday-shipment-popup">
        <div class="saturday-shipment-popup__descriptions">
          <!-- eslint-disable vue/no-v-html -->
          <p
            v-for="(desc, index) in descriptions"
            :key="index"
            class="saturday-shipment-popup__description"
            v-html="formatDescription(desc)"
          />
          <!-- eslint-enable vue/no-v-html -->
        </div>
        <div class="saturday-shipment-popup__options">
          <label
            v-for="option in options"
            :key="String(option.value)"
            class="saturday-shipment-popup__option"
            :class="{
              'saturday-shipment-popup__option--selected':
                selectedValue === option.value,
            }"
          >
            <input
              v-model="selectedValue"
              type="radio"
              :value="option.value"
              name="saturdayShipment"
              class="saturday-shipment-popup__radio"
            >
            <span class="saturday-shipment-popup__label">{{
              option.label
            }}</span>
          </label>
        </div>
      </div>
    </template>
  </Modal>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useStore } from 'vuex';
import { Modal } from '@n11/so-ui-components';
import { ToastMessageTypes } from '@/utils/enum/toastMessageTypes';
import {
  sendSaturdayDeliveryPopUpViewEvent,
  sendSaturdayDeliveryPopUpSuccessEvent,
  sendSaturdayDeliveryPopUpDismissEvent,
} from '@/utils/ga4/helper';

const store = useStore();
const popupModal = ref(null);
const selectedValue = ref(true);
let isClosedByAction = false;

const userId = computed(() => store.getters['auth/user']?.sellerId ?? '');
const popupData = computed(
  () => store.getters['saturdayShipment/getPopupData'],
);
const isSaveLoading = computed(
  () => store.getters['saturdayShipment/isSaveLoading'],
);
const descriptions = computed(
  () => popupData.value?.content?.description || [],
);
const options = computed(() => popupData.value?.options || []);

function formatDescription(desc) {
  if (!desc?.text) return '';
  let { text } = desc;
  desc.bolds?.forEach((bold) => {
    text = text.replace(bold, `<strong>${bold}</strong>`);
  });
  return text;
}

function openPopup() {
  selectedValue.value = true;
  isClosedByAction = false;
  popupModal.value?.openModal();
  sendSaturdayDeliveryPopUpViewEvent({
    page_type: 'Dashboard',
    user_id: userId.value,
  });
}

function handleClose() {
  isClosedByAction = true;
  sendSaturdayDeliveryPopUpDismissEvent({
    page_type: 'Dashboard',
    button_name: 'Kapat',
    user_id: userId.value,
  });
  popupModal.value?.hiddenModal();
}

function onModalClosed() {
  if (!isClosedByAction) {
    sendSaturdayDeliveryPopUpDismissEvent({
      page_type: 'Dashboard',
      button_name: 'X',
      user_id: userId.value,
    });
  }
  isClosedByAction = false;
}

async function handleApply() {
  try {
    const response = await store.dispatch(
      'saturdayShipment/saveSelection',
      selectedValue.value,
    );
    isClosedByAction = true;
    popupModal.value?.hiddenModal();
    sendSaturdayDeliveryPopUpSuccessEvent({
      page_type: 'Dashboard',
      button_name: 'Uygula',
      saturday_delivery_option: selectedValue.value,
      user_id: userId.value,
    });
    store.commit('common/SET_TOASTER_DATA', {
      type: ToastMessageTypes.SUCCESS,
      description: response?.responseMessage || 'Seçiminiz kaydedildi.',
    });
  } catch (error) {
    store.commit('common/SET_TOASTER_DATA', {
      type: ToastMessageTypes.ERROR,
      description:
        error?.data?.responseMessage ||
        'Seçiminiz kaydedilemedi. Lütfen tekrar deneyin.',
    });
  }
}

defineExpose({ openPopup });
</script>

<style lang="scss" scoped>
@import '@/assets/styles/variables';

.saturday-shipment-popup {
  text-align: center;

  &__descriptions {
    margin-bottom: 24px;
  }

  &__description {
    margin-bottom: 24px;
    color: $colorN80;
    font-size: 16px;
    font-weight: 400;
    letter-spacing: -0.05px;
    line-height: 24px;

    &:last-child {
      margin-bottom: 0;
    }

    :deep(strong) {
      font-weight: 700;
    }
  }

  &__options {
    display: flex;
    flex-direction: column;
    margin-top: 16px;
    text-align: left;
  }

  &__option {
    display: flex;
    align-items: center;
    padding: 6px 0;
    cursor: pointer;
    gap: 10px;
  }

  &__radio {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
    margin: 0;
    accent-color: $dark-gray-5;
    cursor: pointer;
  }

  &__label {
    color: $colorN80;
    cursor: pointer;
    font-size: 14px;
    font-weight: 400;
    letter-spacing: -0.05px;
    line-height: 24px;
  }
}
</style>
